import type {
  EcommerceAdd,
  EcommerceCheckout,
  EcommerceCheckoutOption,
  EcommerceEntry,
  EcommercePurchase,
  EcommerceRemove,
} from "./ecommerse";

export type EventName =
  | PageLoadEventType
  | "Product Page Impression"
  | "Webshop Entry Form Button"
  | "Webshop Add More Products Button"
  | "Webshop Edit Details Button"
  | "Webshop Calculate Price Button"
  | "Remove from Cart"
  | "Add to Cart"
  | "Checkout"
  | "Checkout Option"
  | "Checkout Interrupted"
  | "Verify and Buy"
  | "Purchase"
  | "Webshop Add to Cart Button"
  | "Webshop Remove from Cart Button"
  | "Price Calculation"
  | "Insurance Information Updated";

export interface IEvent {
  event: EventName;
}

export const PageImpressionType = {
  EntryPage: "EntryProductPage",
  TopQuestionsPage: "TopQuestionsPage",
  PricePage: "PricePage",
  CheckoutPage: "CheckoutPage",
  ConfirmationPage: "ConfirmationPage",
  CartPage: "CartPage",
} as const;
export type PageImpressionType =
  (typeof PageImpressionType)[keyof typeof PageImpressionType];

export const PageLoadEventType = {
  VirtualPageLoad: "Virtual Page Load",
  PageLoad: "Page Load",
} as const;
export type PageLoadEventType =
  (typeof PageLoadEventType)[keyof typeof PageLoadEventType];

export const PageType = {
  ProductPage: "Product Page",
  WebshopPage: "Webshop Page",
  WebshopConfirmationPage: "Webshop Confirmation Page",
} as const;
export type PageType = (typeof PageType)[keyof typeof PageType];

export const PartnerName = {
  NU: "Nordea",
  WS: "Webshop",
} as const;
export type PartnerName = (typeof PartnerName)[keyof typeof PartnerName];

export interface EnvironmentInfo {
  country: "Finland";
  businessArea: "PRI";
  portfolio: "if.fi";
  partner: string | undefined;
}

export interface PageInfo {
  pageType: string;
  pageLanguage: string;
  pageUrl: string;
  canonicalPageUrl: string | undefined;
  httpStatusCode: number;
}

export interface CustomerInfo {
  loggedIn: boolean;
  customerId: ObjectId;
  age: number | undefined | null;
  gender: string | undefined | null;
  customerStatus: CustomerTypeValue[] | undefined;
  isObjectPossessor: boolean | undefined;
  moving: boolean | undefined;
  email: string | undefined | null;
  phone: string | undefined | null;
  firstName: string | undefined | null;
  lastName: string | undefined | null;
  retired: boolean | undefined;
}

export const CustomerType = {
  New: "New",
  Existing: "Existing",
} as const;
export type CustomerType = (typeof CustomerType)[keyof typeof CustomerType];

export interface CustomerTypeValue {
  type: string;
  value: string;
}

export interface ProductInfo {
  id: number;
  productName: ProductName;
  currencyCode: "EUR";
}

export interface ProductName {
  localName: string;
  localSubName: string | undefined;
  name: string | undefined;
  subName: string | undefined;
}

export interface InsuranceTravel {
  type: string | undefined | null;
  destination: string | undefined;
}

export interface InsuranceInfo {
  price: number | undefined;
  quoteId: string;
  coverage: string | undefined;
  insuranceAddons: InsuranceAddon[] | undefined;
  startdate: string | undefined;
  enddate: string | undefined;
  duedate: string | undefined;
  youngDriver: boolean | undefined;
  mileage: number | undefined;
  duration: number | undefined;
  eventTime: number | undefined;
  insuranceTravel: InsuranceTravel | undefined;
  insuranceDeductable: InsuranceDeductable[] | undefined;
  coveredAmount: string | undefined;
  bonus: Bonus[] | undefined;
}

export interface InsuranceAddon {
  value: string;
}

export interface InsuranceDeductable {
  type: string;
  value: number;
}

export interface Bonus {
  type: string;
  value: string;
}

export const ProcessInfoName = {
  webshop: "webshop",
  BadCredit: "Webshop error page bad credit",
  SsnIncorrect: "Webshop error page Ssn incorrect",
} as const;
export type ProcessInfoName =
  (typeof ProcessInfoName)[keyof typeof ProcessInfoName];

export const ProcessStepName = {
  ProductPage: "productpage",
  WebshopEntry: "webshopentry",
  Price: "price",
  Checkout: "checkout",
  Purchase: "purchase",
  Cart: "shopping cart",
  Error: "error",
} as const;
export type ProcessStepName =
  (typeof ProcessStepName)[keyof typeof ProcessStepName];

export const ProcessStepSubStep = {
  SsnIncorrect: 1,
  BadCredit: 2,
};
export type ProcessStepSubStep =
  (typeof ProcessStepSubStep)[keyof typeof ProcessStepSubStep];

export interface ProcessInfo {
  name: ProcessInfoName | undefined;
  processStep: ProcessStep;
  part: string | undefined;
}

export interface ProcessStep {
  mainStep: ProcessStepName;
  subStep: ProcessStepSubStep | undefined;
  status: "available" | "not available" | undefined;
}

export interface ObjectInfo {
  objectId: ObjectId;
  objectSize: object | undefined;
  objectValue: number | undefined;
  postalCode: string | undefined;
  year: number | undefined;
  leased: boolean | undefined;
  numberOfBuildings: number | undefined;
  brand: string | undefined;
}

export interface ObjectId {
  id: string | undefined | null;
  type: string;
}

export interface ProductPageImpression extends IEvent {
  event: "Product Page Impression";
  productInfo: ProductInfo;
  processInfo: ProcessInfo;
  insuranceInfo: InsuranceInfo[];
  ecommerce: EcommerceEntry;
}

export interface WebshopEntryFormButton extends IEvent {
  event: "Webshop Entry Form Button";
}

export interface AddMoreProductsToBasket extends IEvent {
  event: "Webshop Add More Products Button";
}

export interface WebshopEditDetailsButton extends IEvent {
  event: "Webshop Edit Details Button";
}

export interface WebshopCalculatePriceButton extends IEvent {
  event: "Webshop Calculate Price Button";
}

export interface RemoveFromCart extends IEvent {
  event: "Remove from Cart";
  ecommerce: EcommerceRemove;
}

export interface AddToCart extends IEvent {
  event: "Add to Cart";
  productInfo: ProductInfo;
  objectInfo: ObjectInfo[];
  insuranceInfo: InsuranceInfo;
  ecommerce: EcommerceAdd;
}

export interface CheckoutEvent extends IEvent {
  event: "Checkout";
  productInfo: ProductInfo[];
  insuranceInfo: InsuranceInfo[];
  processInfo: ProcessInfo;
  ecommerce: EcommerceCheckout;
  objectInfo: ObjectInfo[];
}

export interface CheckoutOptionEvent extends IEvent {
  event: "Checkout Option";
  productInfo: ProductInfo[];
  insuranceInfo: InsuranceInfo[];
  processInfo: ProcessInfo;
  ecommerce: EcommerceCheckoutOption;
  objectInfo: ObjectInfo[];
}

export const CheckoutInterruptedType = {
  ErrorPageSsnIncorrectLoad: "ErrorPageSsnIncorrectLoad",
  ErrorPageBadCreditLoad: "ErrorPageBadCreditLoad",
  ErrorPageUnknown: "ErrorPageUnknown",
} as const;
export type CheckoutInterruptedType =
  (typeof CheckoutInterruptedType)[keyof typeof CheckoutInterruptedType];

export interface CheckoutInterruptedEvent extends IEvent {
  event: "Checkout Interrupted";
  type: CheckoutInterruptedType;
  processInfo: ProcessInfo;
}

export interface VerifyAndBuyEvent extends IEvent {
  event: "Verify and Buy";
  ecommerce: EcommerceCheckout;
}

export interface PurchaseEvent extends IEvent {
  event: "Purchase";
  productInfo: ProductInfo[];
  insuranceInfo: InsuranceInfo[];
  processInfo: ProcessInfo;
  ecommerce: EcommercePurchase | undefined;
  objectInfo: ObjectInfo[];
}

export interface AddToCartButton extends IEvent {
  event: "Webshop Add to Cart Button";
}

export interface WebshopRemoveFromCartButton extends IEvent {
  event: "Webshop Remove from Cart Button";
}

export interface PageLoad extends IEvent {
  event: PageLoadEventType;
  environmentInfo: EnvironmentInfo;
  pageInfo: PageInfo;
  productInfo: ProductInfo | undefined;
  processInfo: ProcessInfo | undefined;
  customerInfo: CustomerInfo;
  objectInfo: ObjectInfo[] | undefined;
}

export interface PageLoadMultipleProducts
  extends Omit<PageLoad, "productInfo"> {
  event: PageLoadEventType;
  environmentInfo: EnvironmentInfo;
  pageInfo: PageInfo;
  productInfo: ProductInfo[] | undefined;
  processInfo: ProcessInfo | undefined;
  customerInfo: CustomerInfo;
  objectInfo: ObjectInfo[] | undefined;
  insuranceInfo: InsuranceInfo[];
}

export interface PriceCalculated extends IEvent {
  event: "Price Calculation";
  productInfo: ProductInfo;
  processInfo: ProcessInfo;
  objectInfo: ObjectInfo[];
  insuranceInfo: InsuranceInfo[];
  customerInfo: CustomerInfo;
}

export interface InsuranceInformationUpdated extends IEvent {
  event: "Insurance Information Updated";
  objectInfo: ObjectInfo[];
  customerInfo: CustomerInfo;
  insuranceInfo: InsuranceInfo[];
}
